import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { USERTYPE } from 'src/app/core/enums/routing-item.enum';
import { UtilitiesService } from 'src/app/core/services/utilities.service';
import { API_URL } from 'src/app/shared/enums/api-enums';
import { environment } from 'src/environments/environment';
import { RequestOption } from '../model/common.model';
import { SegmentationModel } from '../model/score-card.model';
import { GetAllResponse, SingleResponse } from './../model/restful-api.model';

@Injectable({
  providedIn: 'root'
})
export class ScoreCardService {

  baseUrl = environment.portalURL;
  constructor(
    private util: UtilitiesService,
    private http: HttpClient
  ) { }

  getGSL(key?): Observable<GetAllResponse<any>> {
    if (key) {
      return this.util.req.httpGet<GetAllResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/filter/gsl?searchKey=' + key);
    } else {
      return this.util.req.httpGet<GetAllResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/filter/gsl');
    }
  }

  getParentCompany(): Observable<GetAllResponse<any>> {
    return this.util.req.httpGet<GetAllResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/filter/parent-company');
  }

  getGSLsByParent(listGSL: string[]): Observable<GetAllResponse<any>> {
    return this.util.req.httpPost<GetAllResponse<any>>(this.baseUrl + API_URL.SCORE_CARD +
      '/filter/gsl-by-parent', {parents: listGSL});
  }

  getCommodities(gslNumbers: string[], timeRangeInput: string, poReceiptMonthInput: number, viewTypeInput: number, parentNames?: string[]):
  Observable<GetAllResponse<any>> {
    const model = {
      parents: parentNames,
      gsl: gslNumbers,
      timeRange: timeRangeInput,
      poReceiptMonth: poReceiptMonthInput,
      viewType: viewTypeInput
    };
    return this.util.req.httpPost<GetAllResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/filter/commodities', model);
  }

  getLocations(): Observable<GetAllResponse<any>> {
    return this.util.req.httpGet<GetAllResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/filter/locations');
  }

  getRegions(): Observable<GetAllResponse<any>> {
    return this.util.req.httpGet<GetAllResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/filter/regions');
  }

  getMinorComponent(): Observable<GetAllResponse<any>> {
    return this.util.req.httpGet<GetAllResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/filter/minor-component');
  }

  getLocationsAndRegions(gslNumbers: string[], timeRangeInput: string, poReceiptMonthInput: number, viewTypeInput: number, parentNames?: string[]):
  Observable<GetAllResponse<any>> {
    const model = {
      parents: parentNames,
      gsl: gslNumbers,
      timeRange: timeRangeInput,
      poReceiptMonth: poReceiptMonthInput,
      viewType: viewTypeInput
    };
    return this.util.req.httpPost<GetAllResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/filter/regions-locations', model);
  }

  query(model, type): Observable<SingleResponse<any>> {
    if (type === USERTYPE.SUPPLIER_USER) {
      return this.util.req.httpPost<SingleResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/report-external', model);
    } else {
      return this.util.req.httpPost<SingleResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/report-external/internal', model);
    }
  }

  queryInternalReport(model, requestOption: RequestOption): Observable<SingleResponse<any>> {
    if (requestOption.pagingOptions.sort.sortBy && requestOption.pagingOptions.sort.sortOrder) {
      model.sortBy = requestOption.pagingOptions.sort.sortBy;
      model.sortDirection = requestOption.pagingOptions.sort.sortOrder;
    }
    model.page = requestOption.pagingOptions.number;
    model.size = requestOption.pagingOptions.size;
    return this.util.req.httpPost<SingleResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/report-internal', model);
  }

  exportExcel(model) {
    return this.http.post(`${this.baseUrl + API_URL.SCORE_CARD}/report-internal/export`, model, {responseType: 'arraybuffer'});
  }

  downloadGuildline(type: string): Observable<SingleResponse<any>> {
    return this.util.req.httpGet<SingleResponse<any>>(`${this.baseUrl + API_URL.SCORE_CARD}/${type}/rating-guideline`);
  }

  getLastUpdate(): Observable<SingleResponse<any>> {
    return this.util.req.httpGet<SingleResponse<any>>(`${this.baseUrl + API_URL.SCORE_CARD}/last-update`);
  }

  getLastReceiptDate(): Observable<SingleResponse<any>> {
    return this.util.req.httpGet<SingleResponse<any>>(`${this.baseUrl + API_URL.SCORE_CARD}/last-receipt-date`);
  }

  downloadRawData(model) {
    return this.http.post(this.baseUrl + API_URL.SCORE_CARD + '/report-external/export/raw', model, {responseType: 'arraybuffer'});
  }

  downloadInternalRawData(phase, model) {
    return this.http.post(this.baseUrl + API_URL.SCORE_CARD + '/report-internal/raw/phase/' + phase, model, {responseType: 'arraybuffer'});
  }

  countPhase(model): Observable<SingleResponse<any>> {
    return this.http.post<SingleResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/report-internal/raw/phase', model);
  }

  downloadRawSupplierPrice(model) {
    return this.http.post(this.baseUrl + API_URL.SCORE_CARD + '/supplier-price/export/raw', model, {responseType: 'arraybuffer'});
  }

  downloadSupplierPrice(model) {
    return this.http.post(this.baseUrl + API_URL.SCORE_CARD + '/supplier-price/export', model, {responseType: 'arraybuffer'});
  }

  getSegmentationMatrixData(model): Observable<GetAllResponse<SegmentationModel>> {
    return this.util.req.httpPost<GetAllResponse<SegmentationModel>>(this.baseUrl + API_URL.SCORE_CARD +
      '/report-internal/segmentation-matrix', model);
  }

  getHeaderValue(model): Observable<SingleResponse<any>> {
    return this.http.post<SingleResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + '/report-internal/header-values', model);
  }

  getSpiderChart(model) {
    return this.util.req.httpPost<GetAllResponse<any>>(this.baseUrl + API_URL.SCORE_CARD +
      '/report-internal/spider-chart', model);
  }

  getLowPerformers(model) {
    return this.util.req.httpPost<GetAllResponse<any>>(this.baseUrl + API_URL.SCORE_CARD + API_URL.LOW_PERFORMANCE, model);
  }

  downloadOTDPromisedDate(model) {
    return this.http.post(this.baseUrl + API_URL.SCORE_CARD + '/otdPromisedDate/export', model, {responseType: 'arraybuffer'});
  }
  downloadOTDNeeds(model) {
    return this.http.post(this.baseUrl + API_URL.SCORE_CARD + '/otdNeeds/export', model, {responseType: 'arraybuffer'});
  }

  downloadLowPerformance(model) {
    return this.http.post(this.baseUrl + API_URL.SCORE_CARD + API_URL.LOW_PERFORMANCE + '/export', model, {responseType: 'arraybuffer'});
  }

  downloadBotReport(model) {
    return this.http.post(`${this.baseUrl + API_URL.SCORE_CARD}/bot-report/export`, model, {responseType: 'arraybuffer'});
  }
}
